import React, { useEffect, useState, useRef, useCallback } from 'react';
import axiosInstance from '../../interceptor/axiosInstance';
import './InsightsMain.css';
import { useNavigate } from 'react-router-dom';
import Insights from './Insights';
import { format } from 'date-fns';
import Swal from 'sweetalert2';

// Skeleton Row Component
const SkeletonRow = () => (
  <tr className="insights-main-skeleton-row">
    <td><div className="insights-main-skeleton-cell"></div></td>
    <td><div className="insights-main-skeleton-cell"></div></td>
    <td><div className="insights-main-skeleton-cell"></div></td>
    <td><div className="insights-main-skeleton-cell"></div></td>
    <td><div className="insights-main-skeleton-cell"></div></td>
    <td><div className="insights-main-skeleton-cell"></div></td>
  </tr>
);

const InsightsMain = () => {
  const [subOrgs, setSubOrgs] = useState([]);
  const [showInsights, setShowInsights] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ next: null, previous: null, count: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(10)

  const [testStatuses, setTestStatuses] = useState({});  // Track `is_active` per test
     const [releaseLoaders, setReleaseLoaders] = useState({});  // Track loading per test ID

  // Refs for debounce and abort controller
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  const navigate = useNavigate();

  // Empty State Component (defined inside to access searchQuery)
  const EmptyState = () => (
    <div className="insights-main-empty-state">
      <div className="insights-main-empty-icon">📊</div>
      <h3 className="insights-main-empty-title">No Tests Found</h3>
      <p className="insights-main-empty-message">
        {searchQuery 
          ? "Try adjusting your search query"
          : "Create your first test to see insights here"}
      </p>
    </div>
  );

  // Fetch data from API with pagination
  const fetchSubOrgs = useCallback((page = 1, search = '') => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setLoading(true);
    axiosInstance
      .get('reports/test_details/', {
        params: { page, page_size: pageSize, name: search }, // Send search query in request
        signal: abortControllerRef.current.signal
      })
      .then(response => {
        const fetchedData = response.data?.data?.results || [];
        setSubOrgs(fetchedData);
  
        // Initialize testStatuses based on fetched data
        const initialStatuses = {};
        fetchedData.forEach((org) => {
          initialStatuses[org.id] = { is_released: org.is_released };
        });
        setTestStatuses(initialStatuses);
  
        setPagination({
          next: response.data?.data?.pagination?.next,
          previous: response.data?.data?.pagination?.previous,
          count: response.data?.data?.pagination?.count,
        });
  
        setLoading(false);
      })
      .catch(error => {
        // Don't show error if request was cancelled
        if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
          console.error('Error fetching data:', error);
        }
        setLoading(false);
      });
  }, [pageSize]);
  
  // Debounced search effect
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search - wait 500ms after user stops typing
    searchTimeoutRef.current = setTimeout(() => {
      fetchSubOrgs(1, searchQuery, pageSize);
    }, 500);

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchQuery, pageSize, fetchSubOrgs]);

  // Separate effect for page changes (no debounce needed)
  useEffect(() => {
    if (currentPage !== 1) {
      fetchSubOrgs(currentPage, searchQuery, pageSize);
    }
  }, [currentPage, searchQuery, pageSize, fetchSubOrgs]);
  
  const handleSave = (id) => {
    setLoading(true);
    navigate('/report', { state: { testId: id } });
  };

  // Handle pagination
  const handleNext = () => {
    if (pagination.next) setCurrentPage(prevPage => prevPage + 1);
  };

  const handlePrevious = () => {
    if (pagination.previous) setCurrentPage(prevPage => prevPage - 1);
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return subOrgs; // If no sorting, return unsorted data
  
    return [...subOrgs].sort((a, b) => {
      let valA = a[sortConfig.key] ?? ""; // Handle undefined values
      let valB = b[sortConfig.key] ?? "";
  
      // Convert date fields to timestamps for proper sorting
      if (['created_at', 'start_date_time', 'end_date_time'].includes(sortConfig.key)) {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
      } else if (typeof valA === 'string' && typeof valB === 'string') {
        valA = valA.toLowerCase();
        valB = valB.toLowerCase();
      }
  
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [subOrgs, sortConfig]); // Ensure dependencies are correct
  

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortArrow = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? '▲' : '▼';
    }
    return '⇅';
  };

  const handleReleaseClick = async (testId) => {
    setReleaseLoaders(prev => ({ ...prev, [testId]: true })); // Set loading only for clicked button
  
    try {
      const currentStatus = testStatuses[testId]?.is_released ?? false;
      const newReleaseStatus = !currentStatus;
  
      // Send the request to toggle `is_released` in the backend
      const response = await axiosInstance.put(`/assessments/tests/${testId}/`, {
        is_released: newReleaseStatus,
      });

      if (response.status === 200) {
        setTestStatuses(prev => ({
          ...prev,
          [testId]: { is_released: newReleaseStatus },
        }));
        Swal.fire('Success', 'Test updated successfully!', 'success');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update the test.';
      const validationErrors = error.response?.data?.errors;
  
      const formattedErrors = validationErrors
        ? Object.entries(validationErrors)
            .map(([key, value]) => `${key}: ${value.join(', ')}`)
            .join('\n')
        : '';
  
      Swal.fire({
        title: 'Error',
        text: `${errorMessage}\n\n${formattedErrors}`,
        icon: 'error',
      });
    } finally {
      setReleaseLoaders(prev => ({ ...prev, [testId]: false })); // Reset loading for clicked button
    }
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleChange = (e) => {
    setPageSize(Number(e.target.value));
  };

  return (
    <div className="insights-main-container">
      {/* Header */}
      <div className="insights-main-header">
        <div>
          <h1 className="insights-main-title">Test Insights</h1>
        </div>
      </div>

      {/* Search and Page Size Controls */}
      <div className="insights-main-controls">
        <input
          type="text"
          placeholder="🔍 Search by test name..."
          value={searchQuery}
          onChange={handleSearch}
          className="insights-main-search-input"
        />

        <div className="insights-main-page-size">
          <label htmlFor="pageSize" className="insights-main-page-size-label">
            Show:
          </label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={handleChange}
            className="insights-main-page-size-select"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="insights-main-table-wrapper">
          <table className="insights-main-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Duration</th>
                <th>Created</th>
                <th>Started</th>
                <th>End Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, idx) => (
                <SkeletonRow key={idx} />
              ))}
            </tbody>
          </table>
        </div>
      ) : showInsights ? (
        <Insights data={analyticsData} />
      ) : sortedData.length === 0 ? (
        <EmptyState />
      ) : (
        <div>
          {/* Table */}
          <div className="insights-main-table-wrapper">
            <table className="insights-main-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className="insights-main-sortable">
                    Name <span className="insights-main-sort-arrow">{getSortArrow('name')}</span>
                  </th>
                  <th onClick={() => handleSort('duration')} className="insights-main-sortable">
                    Duration <span className="insights-main-sort-arrow">{getSortArrow('duration')}</span>
                  </th>
                  <th onClick={() => handleSort('created_at')} className="insights-main-sortable">
                    Created <span className="insights-main-sort-arrow">{getSortArrow('created_at')}</span>
                  </th>
                  <th onClick={() => handleSort('start_date_time')} className="insights-main-sortable">
                    Started <span className="insights-main-sort-arrow">{getSortArrow('start_date_time')}</span>
                  </th>
                  <th onClick={() => handleSort('end_date_time')} className="insights-main-sortable">
                    End Date <span className="insights-main-sort-arrow">{getSortArrow('end_date_time')}</span>
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map(org => (
                  <tr key={org.id} className={org.first_time === false ? 'insights-main-highlight-red' : ''}>
                    <td className="insights-main-td-name">{org.name}</td>
                    <td>{org.duration}</td>
                    <td className="insights-main-td-date">
                      {format(new Date(org.created_at), 'dd-MM-yyyy HH:mm')}
                    </td>
                    <td className="insights-main-td-date">
                      {format(new Date(org.start_date_time), 'dd-MM-yyyy HH:mm')}
                    </td>
                    <td className="insights-main-td-date">
                      {format(new Date(org.end_date_time), 'dd-MM-yyyy HH:mm')}
                    </td>
                    <td className="insights-main-td-actions">
                      <div className="insights-main-action-buttons">
                        <button className="insights-main-details-btn" onClick={() => handleSave(org.id)}>
                          See Details
                        </button>
                        <button
                          className={`insights-main-release-btn ${testStatuses[org.id]?.is_released ? 'insights-main-released' : ''}`}
                          onClick={() => handleReleaseClick(org.id)}
                          disabled={releaseLoaders[org.id]}
                        >
                          {releaseLoaders[org.id]
                            ? 'Releasing...'
                            : testStatuses[org.id]?.is_released === true
                            ? 'Released'
                            : 'Release'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="insights-main-pagination">
            <button
              className="insights-main-pagination-btn"
              onClick={handlePrevious}
              disabled={!pagination.previous}
            >
              Previous
            </button>
            <span className="insights-main-page-info">
              Page {currentPage} of {Math.ceil(pagination.count / pageSize)}
            </span>
            <button
              className="insights-main-pagination-btn"
              onClick={handleNext}
              disabled={!pagination.next}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightsMain;
